import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LABEL, useAuth } from '../lib/auth.tsx';
import { ChangePasswordModal } from './ChangePasswordModal.tsx';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  superadminOnly?: boolean;
}

type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'evshadow-theme';

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: string }[] = [
  { value: 'light', label: 'Claro', icon: '☀' },
  { value: 'dark', label: 'Escuro', icon: '●' },
  { value: 'system', label: 'Sistema', icon: '◐' },
];

function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage indisponível (modo privado, política do navegador etc.) — segue padrão.
  }
  return 'system';
}

function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') {
    const prefersLight =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: light)').matches;
    return prefersLight ? 'light' : 'dark';
  }
  return pref;
}

function applyTheme(pref: ThemePreference) {
  const next = resolveTheme(pref);
  const setAttr = () => document.documentElement.setAttribute('data-theme', next);
  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const canAnimate = !prefersReduced && typeof document.startViewTransition === 'function';
  if (!canAnimate) {
    setAttr();
    return;
  }
  // Crossfade nativo: a API tira uma "foto" da tela antes e depois da troca e
  // faz o blend — sem isso, troca de tema é um corte seco. Quando o navegador
  // pula a transição (aba em segundo plano, trocas em sequência rápida), as
  // três promises (ready/updateCallbackDone/finished) rejeitam com AbortError
  // — cada uma sem handler vira um unhandledrejection próprio, por isso as
  // três precisam de catch, não só uma.
  const transition = document.startViewTransition(setAttr);
  transition.ready.catch(() => {});
  transition.updateCallbackDone.catch(() => {});
  transition.finished.catch(() => {});
}

/** Seletor de tema com 3 estados (claro/escuro/sistema), persistido em localStorage. */
function ThemeToggle() {
  const [pref, setPref] = useState<ThemePreference>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(pref);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      // Sem persistência disponível — o tema ainda se aplica nesta sessão.
    }
  }, [pref]);

  useEffect(() => {
    if (pref !== 'system' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [pref]);

  return (
    <div className="theme-toggle" role="radiogroup" aria-label="Tema do painel">
      {THEME_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={pref === opt.value}
          className={`theme-toggle__btn${pref === opt.value ? ' is-active' : ''}`}
          onClick={() => setPref(opt.value)}
          title={opt.label}
        >
          <span aria-hidden="true">{opt.icon}</span>
          <span className="sr-only">{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

const NAV_PRIMARY: NavItem[] = [
  { to: '/', label: 'Visão geral', icon: '◆' },
  { to: '/acoes', label: 'Ações', icon: '☰' },
  { to: '/calendario', label: 'Calendário', icon: '▦' },
  { to: '/metricas', label: 'Métricas', icon: '◔' },
  { to: '/noticias', label: 'Radar', icon: '◈' },
];

const NAV_SECONDARY: NavItem[] = [
  { to: '/concorrencia', label: 'Concorrência', icon: '⧉' },
  { to: '/auditoria', label: 'Auditoria', icon: '❖' },
  { to: '/usuarios', label: 'Usuários', icon: '⚇', superadminOnly: true },
];

export function Layout() {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<{ top: number; height: number } | null>(null);

  const secondaryItems = NAV_SECONDARY.filter(
    (item) => !item.superadminOnly || can('superadmin'),
  );

  // Fecha o menu mobile ao trocar de rota — evita que o drawer fique
  // aberto "por trás" da tela seguinte.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Barrinha que desliza até o item ativo em vez de só trocar de cor — lida medindo a posição
  // real do link ativo, então acompanha qualquer mudança de altura/zoom sem valores fixos.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('.sidebar__link.is-active');
    if (!active) {
      setIndicator(null);
      return;
    }
    setIndicator({ top: active.offsetTop, height: active.offsetHeight });
  }, [location.pathname, navOpen]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Pular para o conteúdo
      </a>

      <aside className={`sidebar${navOpen ? ' sidebar--nav-open' : ''}`}>
        <div className="sidebar__brand">
          <span className="sidebar__mark" aria-hidden="true">
            EV
          </span>
          <span>
            <strong>EvShadow</strong>
            <span className="text-micro text-muted">Central de operação</span>
          </span>

          <button
            type="button"
            className="sidebar__nav-toggle"
            aria-expanded={navOpen}
            aria-controls="sidebar-nav"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
            <span className="sr-only">{navOpen ? 'Fechar menu' : 'Abrir menu'}</span>
          </button>
        </div>

        <nav className="sidebar__nav" id="sidebar-nav" aria-label="Navegação principal" ref={navRef}>
          {indicator && (
            <span
              className="sidebar__indicator"
              aria-hidden="true"
              style={{ top: indicator.top, height: indicator.height }}
            />
          )}
          {NAV_PRIMARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}
            >
              <span className="sidebar__icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}

          {secondaryItems.length > 0 && (
            <>
              <span className="sidebar__group-label text-micro text-muted">Gestão</span>
              {secondaryItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}
                >
                  <span className="sidebar__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar__footer sidebar__footer--stacked">
          <ThemeToggle />
          <div className="sidebar__footer">
            <div className="sidebar__user">
              <strong className="text-label">{user?.name}</strong>
              <span className="text-micro text-muted">{user ? ROLE_LABEL[user.role] : ''}</span>
            </div>
            <div className="sidebar__user-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setChangingPassword(true)}
              >
                Senha
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void logout()}>
                Sair
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="app-main" id="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
    </div>
  );
}
